import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { select, Store } from '@ngrx/store';

import { isNull } from 'lodash-es';
import { map, Observable, tap } from 'rxjs';

import { AddressEnum } from '../../enums/address';
import { PrimarySubscriptionPlanAccessAliasEnum } from '../../enums/monetization';
import { ProfileSearchEnum } from '../../enums/profile-search';
import { AddressPipe } from '../../misc/pipes/address.pipe';
import { MapsService } from '../../misc/services/maps.service';
import { MonetizationService } from '../../misc/services/monetization.service';
import * as fromMapActions from '../../store/actions/map.actions';
import * as fromPropertyProfileActions from '../../store/actions/property-profile.actions';
import * as fromMapSelectors from '../../store/selectors/map.selectors';
import * as fromPropertyProfileSelectors from '../../store/selectors/property-profile.selectors';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-property-search',
  standalone: false,
  templateUrl: './home.page.html'
})
export class HomePageComponent {
  private addressPipe = inject(AddressPipe);
  private mapsService = inject(MapsService);
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  mapInstance: google.maps.Map | null = null;
  mouseDownTimestamp: Date | null = null;
  searchAddressComponents: google.maps.GeocoderAddressComponent[] | null = null;
  selectedMapTypeId: google.maps.MapTypeId | null = null;

  mapCenter$: Observable<google.maps.LatLngLiteral>;
  mapOptions$: Observable<google.maps.MapOptions>;
  markerOptions$: Observable<google.maps.MarkerOptions>;
  searchAddressComponents$: Observable<google.maps.GeocoderAddressComponent[]>;
  searchLocation$: Observable<google.maps.LatLngLiteral | null>;

  constructor() {
    this.mapOptions$ = this.store.pipe(select(fromMapSelectors.selectOptions));
    this.mapCenter$ = this.store.pipe(select(fromMapSelectors.selectCenter));
    this.store.pipe(select(fromMapSelectors.selectCurrentType)).subscribe((selectedMapTypeId) => {
      this.selectedMapTypeId = selectedMapTypeId;
      this.updateMapType();
    });
    this.searchLocation$ = this.store.pipe(select(fromPropertyProfileSelectors.selectPropertySearchAddressLocation()));
    this.markerOptions$ = this.store.pipe(
      select(fromMapSelectors.selectMarkerOptions),
      map((markerOptions) => ({
        ...markerOptions,
        icon: {
          scaledSize: new google.maps.Size(26, 44),
          url: '/assets/img/pin-home.png'
        }
      }))
    );

    this.searchAddressComponents$ = this.store.pipe(
      select(fromPropertyProfileSelectors.selectPropertySearchAddressComponents),
      tap((data) => {
        this.searchAddressComponents = data;
      })
    );
  }

  async ionViewWillEnter() {
    await this.mapsService.enableWatchPosition();
  }

  async ionViewWillLeave() {
    await this.mapsService.disableWatchPosition();
  }

  mapInitialized(mapInstance: google.maps.Map) {
    this.mapInstance = mapInstance;

    this.updateMapType();

    this.mapInstance.addListener('mousedown', () => {
      this.mouseDownTimestamp = new Date();
    });

    this.mapInstance.addListener('dragstart', () => {
      if (!isNull(this.mouseDownTimestamp)) {
        this.mouseDownTimestamp = null;
      }
    });

    this.mapInstance.addListener('mouseup', (event: google.maps.MapMouseEvent) => {
      if (this.mouseDownTimestamp && new Date().getTime() - this.mouseDownTimestamp.getTime() >= 500) {
        if (event.latLng) {
          this.store.dispatch(
            fromPropertyProfileActions.geocodeCoordinatesLocation({
              payload: { focus: false, latLng: event.latLng.toJSON() }
            })
          );
        }
      }
    });
  }

  mapDragend() {
    if (this.mapInstance) {
      const center = this.mapInstance.getCenter();
      if (center) {
        this.store.dispatch(
          fromMapActions.updateCenter({
            payload: {
              location: center.toJSON()
            }
          })
        );
      }
    }
  }

  updateMapType() {
    if (!isNull(this.selectedMapTypeId)) {
      this.mapInstance?.setMapTypeId(this.selectedMapTypeId);
    }
  }

  async loadProfile() {
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      if (this.searchAddressComponents) {
        this.store.dispatch(
          fromPropertyProfileActions.loadProfileByAddress({
            payload: {
              address: this.addressPipe.transform(this.searchAddressComponents, AddressEnum.LineOne),
              searchType: ProfileSearchEnum.PropertySearch,
              zip: this.addressPipe.transform(this.searchAddressComponents, AddressEnum.ZipFull)
            }
          })
        );
      }
    }
  }

  clearSearch() {
    this.store.dispatch(fromPropertyProfileActions.resetSearch());
  }

  protected readonly addressType = AddressEnum;
}
